/**
 * 技能系统核心类型定义
 *
 * 统一定义技能相关的类型，供技能数据、战斗模块与角色模块复用。
 */

import { z } from 'zod';
import { AttributeKeySchema, type AttributeKey } from '@/IsekaiRPG/modules/character/characterAttributeTypes';
import {
  DEFAULT_COMBAT_TIER_RANK,
  DEFAULT_SKILL_MASTERY_RANK,
  DEFAULT_SKILL_TIER_RANK,
  LibraryMetaSchema,
  LibraryMetaSourceSchema,
  LibraryMetaStatusSchema,
  LibraryMetaUniquenessSchema,
  RankSchema,
  type LibraryMeta,
  type LibraryMetaSource,
  type Rank,
} from '@/IsekaiRPG/shared/types';
import {
  ChainedSkillEffectSchema,
  SkillConditionSchema,
  SkillEffectSchema,
  type ChainedSkillEffect,
  type SkillCondition,
  type SkillEffect,
} from './effectSchemas';
// ============================================
// 1. 技能强度与效果
// ============================================

/**
 * 技能强度阶级 (Rank)
 */
export const SkillTierSchema = RankSchema;
export type SkillTier = Rank;

/**
 * 技能来源
 */
export const SkillSourceSchema = z.enum(['builtin', 'runtime_pool', 'session_cache']);
export type SkillSource = z.infer<typeof SkillSourceSchema>;

const SkillDefinitionLibraryMetaInputSchema = z.object({
  reusable: z.boolean().optional(),
  uniqueness: LibraryMetaUniquenessSchema.optional(),
  source: LibraryMetaSourceSchema.optional(),
  status: LibraryMetaStatusSchema.optional(),
  exposeToAi: z.boolean().optional(),
  tags: z.array(z.string()).optional(),
});

function normalizeSkillDefinitionTags(tags: string[] | undefined): string[] {
  const seen = new Set<string>();
  const normalized: string[] = [];

  (tags || []).forEach(tag => {
    const nextTag = tag.trim();
    if (!nextTag) return;
    const lowerKey = nextTag.toLowerCase();
    if (seen.has(lowerKey)) return;
    seen.add(lowerKey);
    normalized.push(nextTag);
  });

  return normalized;
}

function mapSkillSourceToLibraryMetaSource(source: SkillSource | undefined): LibraryMetaSource {
  if (source === 'builtin') return 'builtin';
  return 'ai_generated';
}

function resolveSkillDefinitionLibraryMeta(input: {
  source?: SkillSource;
  isUnique?: boolean;
  libraryMeta?: Partial<LibraryMeta>;
}): LibraryMeta {
  const explicitTags = normalizeSkillDefinitionTags(input.libraryMeta?.tags);

  return LibraryMetaSchema.parse({
    reusable: true,
    uniqueness: input.isUnique ? 'unique' : 'generic',
    source: mapSkillSourceToLibraryMetaSource(input.source),
    status: 'promoted',
    exposeToAi: true,
    ...input.libraryMeta,
    tags: explicitTags,
  });
}

/**
 * 敌方技能注入池
 * - none: 不参与敌方技能注入
 * - baseline: 基础池
 * - variant: 变体池
 */
export const SkillInjectionPoolSchema = z.enum(['none', 'baseline', 'variant']);
export type SkillInjectionPool = z.infer<typeof SkillInjectionPoolSchema>;

/**
 * 技能主属性（用于战斗主缩放）
 */
export const SkillScalingAttributeSchema = AttributeKeySchema;
export type SkillScalingAttribute = AttributeKey;

// ============================================
// 2. ECA 效果类型
// ============================================

export { ChainedSkillEffectSchema, SkillConditionSchema, SkillEffectSchema };
export type { ChainedSkillEffect, SkillCondition, SkillEffect };

// ============================================
// 3. 静态技能定义 (Blueprint)
// ============================================

const SkillDefinitionObjectSchema = z.object({
  id: z.string(),
  name: z.string(),
  description: z.string(),
  icon: z.string(),
  injectionPool: SkillInjectionPoolSchema.default('none'),
  libraryMeta: SkillDefinitionLibraryMetaInputSchema.optional(),
  ecaTags: z.array(z.string()).optional(),
  apCost: z.number().min(0).max(10).optional(),
  tier: SkillTierSchema.default(DEFAULT_COMBAT_TIER_RANK),
  effects: z.array(SkillEffectSchema).optional(),
  scalingAttribute: SkillScalingAttributeSchema,
  baseCharges: z.number().min(1).optional(),
  source: SkillSourceSchema.optional(),
  isUnique: z.boolean().optional(),
});

export const SkillDefinitionSchema = SkillDefinitionObjectSchema.transform(value => ({
  ...value,
  libraryMeta: resolveSkillDefinitionLibraryMeta({
    source: value.source,
    isUnique: value.isUnique,
    libraryMeta: value.libraryMeta,
  }),
}));
export type SkillDefinitionInput = z.input<typeof SkillDefinitionSchema>;
export type SkillDefinition = z.infer<typeof SkillDefinitionSchema>;

export function getSkillDefinitionTags(skill: Pick<SkillDefinition, 'libraryMeta'>): string[] {
  return normalizeSkillDefinitionTags(skill.libraryMeta.tags || []);
}

// ============================================
// 4. 运行时/持久化状态
// ============================================

/**
 * 角色技能状态 - 用于记录角色的技能等级和经验
 */
export const CharacterSkillSchema = z.object({
  skillId: z.string(),
  /** 当前技能等级 (1-100) */
  level: z.number().min(1).max(100).default(1),
  exp: z.number().min(0).default(0),
  // 潜能/资质：影响 XP 获取倍率 (1.0 = 100%)
  potential: z.number().default(1.0),
  // 动态技能定义：如果是 AI 生成的专属技能，定义存储在这里
  definition: SkillDefinitionSchema.optional(),
});
export type CharacterSkill = z.infer<typeof CharacterSkillSchema>;

/**
 * 运行时状态效果 (Active Effect)
 */
export const StatusEffectSchema = z.object({
  effectId: z.string(),
  name: z.string().optional(),
  icon: z.string().optional(),
  type: z.enum(['buff', 'debuff']).optional(),
  remainingTurns: z.number().optional(),
  stacks: z.number().default(1),
});
export type StatusEffect = z.infer<typeof StatusEffectSchema>;

// ============================================
// 6. AI 生成载体 (Payload)
// ============================================

/**
 * AI 返回的 JSON 结构预期 (Zod Schema)
 * 用于 ResponseParser 校验 LLM 的原始输出
 *
 * v2.0: AI 直接输出 ECA 格式的 effects，不再使用 intent 中间层
 */
export const AISkillPayloadSchema = z.object({
  name: z.string(),
  description: z.string(),
  icon: z.string().default('✨'),
  tags: z.array(z.string()).default([]),
  scalingAttribute: SkillScalingAttributeSchema.optional(),

  tier: SkillTierSchema.default(DEFAULT_SKILL_TIER_RANK),
  mastery: SkillTierSchema.default(DEFAULT_SKILL_MASTERY_RANK),

  /** ECA 效果列表（AI 直接生成） */
  effects: z.array(SkillEffectSchema).default([]),
});

/**
 * 从 Schema 推导出的 Payload 类型
 */
export type AISkillPayload = z.infer<typeof AISkillPayloadSchema>;
